import { Checkbox, CheckboxGroup } from '@sbab-ui/components'
import { useEffect, useRef, useState } from 'react'
import { AppAction } from '../../appState'
import useCheckboxForm from '../../hooks/useCheckboxForm'
import FormWrapper from './FormWrapper'

interface Props {
  id: string
  values: string[]
  appDispatch: React.Dispatch<AppAction>
  label: string
  maxLength?: number
  step?: number
  placeholder?: string
  options: { id: number; label: string; value: string }[]
  validationFunction: (values: string[]) => string
  isFetching?: boolean
}

export default function FormWithCheckboxes({
  id,
  values,
  appDispatch,
  label,
  step,
  options,
  validationFunction,
  isFetching = false,
}: Readonly<Props>) {
  const { inputRef, handledValues, errorMessage, hasBeenTouched, onChangeHandler, submitHandler } =
    useCheckboxForm({
      initialValues: values,
      appDispatch,
      id,
      validationFunction,
    })
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      submitButtonRef.current?.click()
    }
  }
  const [isBigScreen, setIsBigScreen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsBigScreen(window.innerWidth > 1420)
    }
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const disabled = !!errorMessage || !hasBeenTouched
  const loading = isFetching
  const generateAriaLabel = (label: string) => `${label} checkbox`

  const sortedOptions = options.slice().sort((a, b) => a.label.localeCompare(b.label))

  return (
    <FormWrapper
      id={id}
      appDispatch={appDispatch}
      step={step}
      label={label}
      errorMessage={errorMessage}
      inputRef={inputRef}
      submitButtonRef={submitButtonRef}
      submitHandler={submitHandler}
      disabled={disabled}
      loading={loading}
    >
      <CheckboxGroup
        name="group-options"
        onChange={onChangeHandler}
        values={handledValues}
        onKeyPress={handleKeyPress}
        row={isBigScreen}
      >
        {sortedOptions.map((option, index) => (
          <Checkbox
            key={option.id}
            checked={values.includes(option.value)}
            name={id}
            label={option.label}
            value={option.value}
            ref={index === 0 ? inputRef : undefined}
            aria-label={generateAriaLabel(option.label)}
            className={isBigScreen ? '!mr-0 w-1/2 gap-4 pb-10' : 'w-full p-4'}
          />
        ))}
      </CheckboxGroup>
    </FormWrapper>
  )
}
